import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { AttackPathLegend } from '@/organisms/AttackPathLegend';

/**
 * InvestigationLayout
 * Classification: custom (template)
 *
 * Split investigation view: a large canvas area (attack-path graph / timeline)
 * with a docked side panel for details and the AttackPathLegend.
 */
export interface InvestigationLayoutProps {
  title?: string;
  /** Main canvas content (graph, map, timeline). */
  canvas: ReactNode;
  /** Side panel content (selected node details). */
  panel?: ReactNode;
  /** Show the attack-path legend in the panel. */
  showLegend?: boolean;
}

export function InvestigationLayout({
  title = 'Investigation',
  canvas,
  panel,
  showLegend = true,
}: InvestigationLayoutProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
      <Typography variant="h5" sx={{ fontFamily: 'inherit' }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {canvas}
        </Box>
        <Box
          sx={{
            width: 320,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {panel}
          {showLegend ? (
            <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Legend
              </Typography>
              <AttackPathLegend orientation="vertical" />
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
